import { supabase } from "@/integrations/supabase/client";
import type { Topic } from "./topics";

export type WeeklyVignette = {
  week_start: string;
  title: string | null;
  author: string | null;
  source_name: string | null;
  source_url: string | null;
  excerpt: string | null;
  why_read: string | null;
  kind: string | null;
};

// Ugens søndagsbrev – seneste vignette. Bruges på forsiden som "Ugens fund".
export async function fetchCurrentVignette(): Promise<WeeklyVignette | null> {
  const { data, error } = await (supabase as any)
    .from("weekly_vignettes")
    .select("week_start, title, author, source_name, source_url, excerpt, why_read, kind")
    .not("title", "is", null)
    .not("source_url", "is", null)
    .order("week_start", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return null;
  return (data as WeeklyVignette) ?? null;
}

export async function fetchActiveQuote() {
  const { data, error } = await supabase
    .from("quotes")
    .select("*")
    .eq("active", true)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function fetchArticles(opts?: { topic?: Topic; hidePaywall?: boolean; limit?: number }) {
  let q = supabase.from("articles").select("*").order("created_at", { ascending: false });
  if (opts?.topic) q = q.eq("topic", opts.topic as never);
  if (opts?.hidePaywall) q = q.eq("paywall", false);
  if (opts?.limit) q = q.limit(opts.limit);
  const { data, error } = await q;
  if (error) throw error;
  const filtered = (data ?? []).filter((a) => isAcceptableArticleUrl(a.original_url));
  return deduplicateArticles(filtered);
}

export async function fetchArticle(id: string) {
  const { data, error } = await supabase.from("articles").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return data;
}

export type RecipeRow = {
  id: string;
  title: string;
  chef: string | null;
  source: string | null;
  original_url: string | null;
  paywall: boolean;
  summary: string;
  ingredients: string | null;
  method: string | null;
  image_url: string | null;
  category: string | null;
  tradition: string | null;
  created_at: string;
};

export async function fetchRecipes(): Promise<RecipeRow[]> {
  const { data, error } = await supabase.from("recipes").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as RecipeRow[];
}

export async function fetchRecipe(id: string) {
  const { data, error } = await supabase.from("recipes").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return data;
}

// Ugens opskrift – nyeste opskrift med billede fra sidste 7 dage.
// Bruges af forsiden. Nyhedsbrev og vignet bruger service-role-versionen i
// src/lib/discover-recipes.server.ts.
export async function fetchFeaturedRecipeOfWeek(): Promise<RecipeRow | null> {
  const sinceIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const { data } = await supabase
    .from("recipes")
    .select("*")
    .gte("created_at", sinceIso)
    .not("image_url", "is", null)
    .order("created_at", { ascending: false })
    .limit(1);
  if (data && data[0]) return data[0] as RecipeRow;
  const { data: fallback } = await supabase
    .from("recipes")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(1);
  return ((fallback && fallback[0]) as RecipeRow | undefined) ?? null;
}


export async function fetchPlaylists() {
  const { data, error } = await supabase.from("playlists").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export function isAcceptableArticleUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "").toLowerCase();
    if (host === "instagram.com" || host.endsWith(".instagram.com")) return false;
    if (host === "clausmayer.dk") return false;
    if (u.pathname === "" || u.pathname === "/") return false;
    return true;
  } catch {
    return false;
  }
}

const LOCALE_CODES = new Set([
  "se", "is", "dk", "no", "fi", "en", "de", "fr", "es", "it", "nl", "pl",
  "sv", "da", "nb", "nn", "ru", "pt", "cz", "hu", "ro", "gr", "tr",
]);

function canonicalUrlKey(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "").toLowerCase();
    const path = u.pathname.replace(/\/$/, "");
    const lastSegment = path.split("/").pop() ?? "";
    if (LOCALE_CODES.has(lastSegment.toLowerCase())) {
      const basePath = path.slice(0, path.length - lastSegment.length).replace(/\/$/, "");
      return `${host}${basePath}`;
    }
    return `${host}${path}`;
  } catch {
    return url;
  }
}

export function deduplicateArticles<T extends { original_url: string }>(articles: T[]): T[] {
  const groups = new Map<string, T[]>();
  for (const a of articles) {
    const key = canonicalUrlKey(a.original_url);
    const existing = groups.get(key) ?? [];
    existing.push(a);
    groups.set(key, existing);
  }

  const result: T[] = [];
  for (const group of groups.values()) {
    if (group.length === 1) {
      result.push(group[0]);
      continue;
    }
    const danish = group.find((a) => {
      try {
        return new URL(a.original_url).pathname.toLowerCase().startsWith("/dk");
      } catch {
        return false;
      }
    });
    if (danish) {
      result.push(danish);
      continue;
    }
    const withDa = group.find((a) => {
      try {
        const p = new URL(a.original_url).pathname.toLowerCase();
        return p.endsWith("/da") || p.includes("/da/");
      } catch {
        return false;
      }
    });
    if (withDa) {
      result.push(withDa);
      continue;
    }
    // ellers den med korteste URL (typisk rodomænet uden landekode)
    const shortest = group.sort((a, b) => a.original_url.length - b.original_url.length)[0];
    result.push(shortest);
  }
  return result;
}

export async function fetchNewSinceYesterday() {
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from("articles")
    .select("*")
    .gte("created_at", since)
    .order("created_at", { ascending: false });
  if (error) throw error;
  const filtered = (data ?? []).filter((a) => isAcceptableArticleUrl(a.original_url));
  return deduplicateArticles(filtered);
}

// Friske strejf til forsiden: alle artikler fra i dag + i går
// (Europe/Copenhagen), så aftenens discovery-kørsel lægges oven i morgenens.
// Hvis vinduet er tomt (fx credit-loft har blokeret dagens kørsel), udvider
// vi til seneste 72 timer, så forsiden aldrig står helt bar mens vi venter
// på næste vellykkede kørsel.
export async function fetchFreshStrejf() {
  const now = new Date();
  const startYesterday = new Date(now);
  startYesterday.setUTCHours(0, 0, 0, 0);
  startYesterday.setUTCDate(startYesterday.getUTCDate() - 1);
  const since = new Date(startYesterday.getTime() - 2 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from("articles")
    .select("*")
    .gte("created_at", since)
    .order("created_at", { ascending: false });
  if (error) throw error;
  let filtered = (data ?? []).filter((a) => isAcceptableArticleUrl(a.original_url));

  if (filtered.length === 0) {
    const fallbackSince = new Date(now.getTime() - 72 * 60 * 60 * 1000).toISOString();
    const { data: fallback } = await supabase
      .from("articles")
      .select("*")
      .gte("created_at", fallbackSince)
      .order("created_at", { ascending: false });
    filtered = (fallback ?? []).filter((a) => isAcceptableArticleUrl(a.original_url));
  }

  return deduplicateArticles(filtered);
}


// Alle artikler oprettet på en given kalenderdag (YYYY-MM-DD, Europe/Copenhagen).
// Bruges af /dagbog/$date-arkivet.
export async function fetchArticlesForDate(date: string) {
  // Fortolk datoen i Europa/København som [00:00, 24:00). En simpel offset-baseret
  // beregning er tilstrækkelig præcis for arkivsider.
  const start = new Date(`${date}T00:00:00+01:00`);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  const { data, error } = await supabase
    .from("articles")
    .select("*")
    .gte("created_at", start.toISOString())
    .lt("created_at", end.toISOString())
    .order("created_at", { ascending: false });
  if (error) throw error;
  const filtered = (data ?? []).filter((a) => isAcceptableArticleUrl(a.original_url));
  return deduplicateArticles(filtered);
}

// Deterministisk citat pr. dato – vælger blandt aktive citater ud fra dagnummer,
// så samme dato altid viser samme citat i arkivet.
export async function fetchQuoteForDate(date: string) {
  const { data, error } = await supabase
    .from("quotes")
    .select("*")
    .eq("active", true)
    .order("created_at", { ascending: true });
  if (error) throw error;
  const list = data ?? [];
  if (list.length === 0) return null;
  const d = new Date(`${date}T00:00:00Z`);
  const dayNumber = Math.floor(d.getTime() / (24 * 60 * 60 * 1000));
  return list[((dayNumber % list.length) + list.length) % list.length];
}


export type EventRow = {
  id: string;
  title: string;
  description: string;
  topic: string;
  venue: string | null;
  city: string | null;
  starts_at: string;
  ends_at: string | null;
  url: string;
  source: string;
};

// Samme sociale-medie-filter som discovery, duplikeret her så klienten kan
// bruge det uden at hive server-only kode ind.
const CLIENT_SOCIAL_HOSTS = [
  "instagram.com", "facebook.com", "fb.com", "fb.me", "tiktok.com",
  "twitter.com", "x.com", "threads.net", "threads.com",
  "snapchat.com", "pinterest.com", "bsky.app", "mastodon.social",
  "youtube.com", "youtu.be", "t.me", "telegram.me", "whatsapp.com",
  "linkedin.com", "tumblr.com", "reddit.com",
];
const CLIENT_SOCIAL_MARKERS = ["instagram", "facebook", "tiktok", "twitter", "threads", "snapchat", "pinterest", "youtube", "linkedin", "reddit"];
function isSocialRow(r: { url?: string | null; source?: string | null }): boolean {
  if (r.url) {
    try {
      const host = new URL(r.url).hostname.replace(/^www\./, "").toLowerCase();
      if (CLIENT_SOCIAL_HOSTS.some((s) => host === s || host.endsWith("." + s))) return true;
    } catch { /* ignore */ }
  }
  if (r.source) {
    const s = r.source.toLowerCase();
    if (CLIENT_SOCIAL_MARKERS.some((m) => s.includes(m))) return true;
  }
  return false;
}

// Defensivt filter: hvis en URL peger på en forside/kalenderoversigt slipper
// vi den ikke gennem – også selvom den ligger i databasen fra før filtret.
const CLIENT_LISTING_MARKERS = [
  "kalender", "event-kalender", "events-kalender", "events", "program",
  "det-sker", "hvad-sker-der", "kommende", "arrangementer", "arrangement-oversigt",
  "whats-on", "what-s-on", "agenda", "guide", "artikler", "blog", "nyheder",
  "list", "oversigt", "kalenderen",
];
function isListingRow(r: { url?: string | null }): boolean {
  if (!r.url) return true;
  try {
    const u = new URL(r.url);
    const segments = u.pathname.split("/").map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (segments.length === 0) return true;
    const last = segments[segments.length - 1];
    if (CLIENT_LISTING_MARKERS.includes(last)) return true;
    const host = u.hostname.replace(/^www\./, "").toLowerCase();
    if (/^visit[a-z-]+\.dk$/.test(host)) {
      const hasDeep = segments.some((s) => s.includes("-") && s.length >= 8);
      if (!hasDeep) return true;
    }
    return false;
  } catch {
    return true;
  }
}

export async function fetchThisWeekEvents(): Promise<EventRow[]> {
  const now = new Date();
  const day = now.getUTCDay();
  const daysFromMonday = (day + 6) % 7;
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - daysFromMonday));
  const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
  const { data, error } = await (supabase as any)
    .from("events")
    .select("*")
    .gte("starts_at", start.toISOString())
    .lt("starts_at", end.toISOString())
    .order("starts_at", { ascending: true });
  if (error) throw error;
  const filtered = ((data ?? []) as EventRow[]).filter((r) => !isSocialRow(r) && !isListingRow(r));
  // Cap på 3 pr. kalenderdag (Europe/Copenhagen ≈ UTC+2 sommer / +1 vinter,
  // grov gruppering er tilstrækkelig her).
  const perDay = new Map<string, number>();
  const out: EventRow[] = [];
  for (const r of filtered) {
    const local = new Date(new Date(r.starts_at).getTime() + 2 * 60 * 60 * 1000);
    const k = local.toISOString().slice(0, 10);
    const n = perDay.get(k) ?? 0;
    if (n >= 3) continue;
    perDay.set(k, n + 1);
    out.push(r);
  }
  return out;
}


export type VideoRow = {
  id: string;
  provider: "youtube" | "vimeo";
  video_id: string;
  url: string;
  title: string;
  description: string;
  topic: Topic;
  channel: string | null;
  thumbnail_url: string | null;
  duration_seconds: number | null;
  published_at: string | null;
  language: string;
  created_at: string;
};

export async function fetchRecentHighlights(limit = 3) {
  const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from("articles")
    .select("*")
    .gte("created_at", since)
    .order("created_at", { ascending: false });
  if (error) throw error;
  const filtered = (data ?? []).filter((a) => isAcceptableArticleUrl(a.original_url));
  const deduped = deduplicateArticles(filtered);

  const seenTopics = new Set<string>();
  const highlights: typeof deduped = [];
  for (const a of deduped) {
    if (!seenTopics.has(a.topic) && highlights.length < limit) {
      seenTopics.add(a.topic);
      highlights.push(a);
    }
  }
  for (const a of deduped) {
    if (highlights.length < limit && !highlights.includes(a)) {
      highlights.push(a);
    }
  }
  return highlights;
}

export async function fetchVideos(opts?: { topic?: Topic; limit?: number }): Promise<VideoRow[]> {
  let q = supabase.from("videos").select("*").order("created_at", { ascending: false });
  if (opts?.topic) q = q.eq("topic", opts.topic as never);
  if (opts?.limit) q = q.limit(opts.limit);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as VideoRow[];
}


